import * as React from "react";
import type { ContextValue, PressEvent } from "react-aria-components";
import type { baseAvatarStyle } from "../Avatar.css.ts";

export interface AvatarItem {
  /** User's full name. Used to generate initials when no image is provided, and as default tooltip text. */
  name?: string;
  /** URL of the profile image to display. */
  src?: string;
  /**
   * Tooltip text shown on hover. Falls back to the `name` prop if not specified.
   * Set to `false` to disable the tooltip.
   */
  tooltip?: string | false;
  /** Alt text for the avatar image. */
  alt?: string;
  /** Custom icon component to display instead of the default user icon placeholder. */
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** URL to navigate to when avatar is clicked. Renders as a link. */
  href?: string;
  /** Callback fired when avatar is clicked. */
  onPress?: (event: PressEvent) => void;
}

export type Size = keyof typeof baseAvatarStyle.classNames.variants.size;

export interface AvatarProps extends AvatarItem {
  /** Size of the avatar. */
  size?: Size;
  /** Visual state indicating the avatar is currently selected or active. */
  active?: boolean;
  /** Force interactive styling. Automatically enabled when `href` or `onPress` is provided. */
  isInteractive?: boolean;
  /** Show a border around the avatar. */
  showBorder?: boolean;
}

export interface InternalAvatarProps {
  insideInputBuilder?: boolean;
}

type AvatarContextProps = AvatarProps & InternalAvatarProps;

export const AvatarContext = React.createContext<
  ContextValue<AvatarContextProps, HTMLDivElement>
>({});
